<template>
	<Panel
		title="Deal Documents"
		:action="headerActions"
		class="panel-uploaded-docs ignore-deal-lock"
	>
		<template #header>
			<LoaderComponent v-show="isBusy" />
		</template>

		<!--Document List-->
		<section
			v-show="currentScreen === CurrentView.DocsList"
			class="docs-list"
		>
			<RichTable
				class="overflow"
				:tableData="docList"
				:headers="TABLE_HEADERS"
				:isBusy="isBusy"
				noData="No Documents Uploaded"
			>
				<template #associations="row">
					<button
						v-show="$global.isAdminView"
						class="button-span button-edit-doc"
						title="Edit Associations"
						@click="openAssociationsEditor(row)"
					>
						<i class="fas fa-pencil-alt" />
					</button>
					<span
						v-for="(association, index) in row.associations"
						class="associations-tag"
						:key="association.id"
						:style="`--index: ${index};`"
					>
						{{ getAssociationDisplay(association) }}
					</span>
				</template>
				<template #previewButton="row">
					<button
						class="ignore-all-locks"
						type="button"
						@click="() => openDocumentPreviewModal(row)"
					>
						Preview
					</button>
				</template>
				<template #editIcons="row">
					<fieldset class="document-actions">
						<button
							title="Delete Document"
							class="button-span button-delete-doc"
							:disabled="!isAssociatedWithDeal(row.associations)"
							@click="() => deleteDocumentRow(row)"
						>
							<i class="fas fa-trash-alt" />
						</button>
					</fieldset>
				</template>
			</RichTable>
		</section>

		<!--Upload New Document-->
		<section
			v-show="currentScreen === CurrentView.UploadDoc"
			class="docs-add-edit"
		>
			<fieldset class="docs-add-edit-fieldset">
				<div class="file-metadata-form">
					<div
						class="flex-side-by-side"
						style="gap: 10px"
					>
						<h3>Upload Method:</h3>

						<InputRadio
							label="Saturn Upload"
							v-model="uploadMethod"
							valueMatch="saturn"
							:invalid="v$.uploadMethod.$invalid"
							:isButton="true"
						/>

						<InputRadio
							label="Mobile Upload"
							v-model="uploadMethod"
							valueMatch="mobile"
							:invalid="v$.uploadMethod.$invalid"
							:isButton="true"
						/>
					</div>

					<InputRichDropdown
						label="Document Type"
						v-model:saturn="currentDoc.docType"
						:list="DOCUMENT_TYPES"
						:valueMap="(d: IDocumentType) => d.code"
						:display="(d: IDocumentType) => d.display"
						:invalid="v$.currentDoc.docType.$invalid"
						:containerHeight="440"
						:sortMode="ENUMS.DROPDOWN_SORT_MODE.ORIGINAL_ORDER"
						search
					/>

					<InputRichDropdown
						v-show="uploadMethod === 'mobile'"
						v-model:saturn="sendLinkTo"
						label="Send Link To"
						:list="sendToList"
						:valueMap="(s: any) => s.personType"
						:display="(s: any) => s.display"
						:invalid="v$.sendLinkTo.$invalid"
						:sortMode="ENUMS.DROPDOWN_SORT_MODE.ORIGINAL_ORDER"
						search
					/>
				</div>

				<div class="file-upload-form">
					<InputFile
						filesAccepted="image/*, application/pdf"
						:key="inputFileKey"
						showFilePreview
						:invalid="v$.currentDoc.fileData.$invalid"
						:disabled="uploadMethod === 'mobile'"
						@fileDataChange="onFileDataChange"
					/>
				</div>
			</fieldset>
		</section>

		<!-- Associations Editor -->
		<section
			v-show="currentScreen === CurrentView.EditAssociations"
			class="docs-associations-editor"
		>
			<div
				v-if="selectedDocToEdit"
				class="basic-doc-info"
			>
				<DocumentPreview
					style="min-width: 250px; max-width: 600px"
					:document="selectedDocToEdit"
					expandOnClick
				/>

				<table class="borderless-table">
					<tbody>
						<tr>
							<th>File Name</th>
							<td>{{ selectedDocToEdit.docInfo.fileName }} ({{ selectedDocToEdit.docInfo.fileType }})</td>
						</tr>
						<tr>
							<th>Source</th>
							<td>{{ selectedDocToEdit.docInfo.source }}</td>
						</tr>
						<tr>
							<th>Date Uploaded</th>
							<td>{{ util.formatDate(selectedDocToEdit.docInfo.timestamp, 'medium', 'medium') }}</td>
						</tr>
						<tr>
							<th>Associations</th>
							<td>
								<div class="list-of-associations">
									<span
										v-for="(association, index) in fullAssociationsList"
										class="associations-tag hovered-desc top"
										:key="association.id"
										:style="`--index: ${index};`"
										:data-title="`ID: ${association.id}`"
									>
										{{ getAssociationDisplay(association) }}

										<button
											class="button-span"
											:disabled="fullAssociationsList.length < 2"
											@click="deleteAssociation(association)"
										>
											<i class="fas fa-times" />
										</button>
									</span>
								</div>
								<div class="add-new-association">
									<InputRichDropdown
										label="Association Type"
										v-model:saturn="newAssociationType"
										:list="ASSOCIATION_TYPES"
										:display="(v: any) => v.display"
										:valueMap="(v: any) => v.value"
										search
									/>
									<InputTextbox
										label="Association ID"
										v-model:saturn="newAssociationId"
										:isUpperCase="false"
									/>

									<button
										:disabled="!newAssociationId || !newAssociationType"
										@click="addNewAssociation"
									>
										<i class="fas fa-plus" />
									</button>
								</div>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
			<div v-else>No document was selected.</div>
		</section>
	</Panel>
</template>
<script lang="ts">
	export interface IPanelUploadedDocumentsProps {
		fimenu: FIMenu;
	}
</script>
<script setup lang="ts">
	import { computed, ref } from 'vue';

	import { required, requiredIf } from '@vuelidate/validators';
	import { useVuelidate } from '@vuelidate/core';

	import $modal from '@core/services/modal';
	import api from '@core/services/api/_api';
	import auth from '@core/services/auth';
	import settings from 'settings';
	import util from '@core/services/util';

	import { DocAssociation, PersonType } from '@core/classes/SharedEnums';
	import { DocumentAuditLogStatus, IDocumentInfo, IUploadedDocument } from '@core/classes/UploadedDocument';
	import { IDocumentUpsertRequestSingle, IGetDocumentUploadPortalLinkRequest } from '@core/services/api/api.documents';
	import { AxiosResponse } from 'axios';
	import DocumentAssociation from '@core/classes/DocumentAssociation';
	import ENUMS from '@core/classes/Enums';
	import { EnumUtil } from '@core/_enums/EnumUtil';
	import FIMenu from '@core/classes/FIMenu';
	import IDocumentType from '@core/_interfaces/DocumentType';
	import ISimpleResponse from '@core/classes/Responses/SimpleResponse';

	import modalDocumentPreview from '@core/modals/modalDocumentPreview.vue';
	import modalInfo from '@core/modals/modalInfo.vue';
	import modalSendLinkToCustomer from '@core/modals/modalSendLinkToCustomer.vue';

	import DocumentPreview from '@core/components/DocumentPreview.vue';
	import InputFile from '@core/components/InputFile.vue';
	import InputRadio from '@core/components/InputRadio.vue';
	import InputRichDropdown from '@core/components/InputRichDropdown.vue';
	import InputTextbox from '@core/components/InputTextbox.vue';
	import LoaderComponent from '@core/components/UI/LoaderComponent.vue';
	import Panel from '@core/components/Panel.vue';
	import RichTable from '@core/components/RichTable.vue';

	interface ICurrentDocData {
		fileData?: File;
		fileType?: string;
		fileName?: string;
		docType?: string;
	}

	enum CurrentView {
		DocsList,
		UploadDoc,
		EditAssociations,
	}

	// #region Constants
	const EMPLOYEE_INFO = auth.getTokenPayload();

	const DOCUMENT_TYPES: IDocumentType[] = settings.lookups.uploadedDocumentTypes;

	const ASSOCIATION_TYPES = EnumUtil.getEnumOptionsWithoutDescription(DocAssociation).filter(e => {
		return ![DocAssociation.Admin, DocAssociation.Other, DocAssociation.RedirectInfo, DocAssociation.FraudCheck].includes(e.value);
	});

	const TABLE_HEADERS: any[] = [
		{
			display: 'Associations',
			name: 'associations',
			slot: 'associations',
			cellCssClass: 'associations-cell',
			value: (r: IUploadedDocument) => {
				return r.associations.map(a => getAssociationDisplay(a));
			},
			autoFilter: true,
		},
		{
			display: 'Created By',
			name: 'createdBy',
			value: (r: IUploadedDocument) => {
				const createdLog = r.auditTrail.find(a => a.status === DocumentAuditLogStatus.Created);
				return createdLog?.statusUpdatedBy ?? 'Unknown';
			},
			autoFilter: true,
			sortable: true,
		},
		{
			display: 'Document',
			name: 'docType',
			value: (r: IUploadedDocument) => {
				if (r.docInfo.docType.startsWith('CUSTOM_')) return util.toProperCase(r.docInfo.docType.substring(6).replaceAll('_', ' '));

				return DOCUMENT_TYPES.find(t => t.code === r.docInfo.docType)?.display ?? 'Unknown';
			},
			autoFilter: true,
			sortable: true,
		},
		{
			display: 'Date Uploaded',
			name: 'timestamp',
			value: (r: IUploadedDocument) => {
				return new Date(r.docInfo.timestamp).toLocaleString('en-US', {
					dateStyle: 'short',
					timeStyle: 'short',
				});
			},
			sortable: true,
		},
		{ display: '', name: 'preview', slot: 'previewButton' },
		{ display: '', name: 'edit', slot: 'editIcons' },
	];
	// #endregion

	const props = defineProps<IPanelUploadedDocumentsProps>();

	const isBusy = ref(false);

	// #region Current Doc Data
	const currentDoc = ref<ICurrentDocData>({
		fileData: null,
		fileType: null,
		fileName: null,
		docType: null,
	});
	const inputFileKey = ref(0); // Used to force a re-render on InputFile

	const onFileDataChange = (fileObject: File) => {
		currentDoc.value.fileData = fileObject;
		currentDoc.value.fileType = fileObject.type;
		currentDoc.value.fileName = fileObject.name;
	};

	const resetCurrentDoc = () => {
		currentDoc.value.fileName = null;
		currentDoc.value.fileData = null;
		currentDoc.value.docType = null;

		uploadMethod.value = null;
		sendLinkTo.value = null;

		inputFileKey.value++; // Force re-render
	};

	const openDocumentPreviewModal = (row: IUploadedDocument) => {
		$modal.open(modalDocumentPreview, {
			name: 'modalDocumentPreview',
			passedData: {
				title: row.docInfo.fileName,
				document: row,
			},
			backdrop: true,
		});
	};

	const addNewDocument = async () => {
		const isFileValid = settings.lookups.uploadedDocumentsAcceptedFileTypes.includes(currentDoc.value.fileType);

		if (!isFileValid) return util.toastr('error', 'Upload Error', 'File type not supported.');

		isBusy.value = true;
		try {
			currentDoc.value.fileName = formatFileNameForAzure(currentDoc.value);
			const docInfo: Partial<IDocumentInfo> = {
				fileName: currentDoc.value.fileName,
				fileType: currentDoc.value.fileType,
				docType: currentDoc.value.docType,
				source: 'FI MENU UPLOAD',
			};

			const uploadRequest: IDocumentUpsertRequestSingle = {
				fileData: currentDoc.value.fileData,
				associations: [
					new DocumentAssociation({
						id: props.fimenu.id,
						type: ENUMS.DOC_ASSOCIATION.Deal,
					}),
				],
				docInfo: docInfo,
				creatorType: DocAssociation.Employee,
				createdBy: getFormattedEmployeeName(),
				creatorId: EMPLOYEE_INFO.EmployeeId,
			};

			const response = await api.documents.upsertDocumentFromRequest(uploadRequest);
			if (!response?.data || !response.data.success)
				throw new Error(response?.data?.message ?? 'No response from server.', {
					cause: response,
				});

			util.toastr('success', 'Upload Successful', 'Successfully uploaded document to deal.');
			await getDocumentList();
			changeScreenTo(CurrentView.DocsList);
		} catch (err) {
			console.error('UNABLE TO UPLOAD NEW DOCUMENT: ', err);
			util.toastr('error', 'Upload Error', (err as Error)?.message ?? 'An unknown server error occurred. Unable to upload document to deal.');
		} finally {
			isBusy.value = false;
		}
	};

	const deleteDocumentRow = (row: IUploadedDocument) => {
		$modal.open(modalInfo, {
			name: 'modalInfo',
			passedData: {
				info: `Document "${row.docInfo.fileName}" is about to be deleted from this deal. Do you want to continue?`,
				acceptText: 'Delete',
				acceptTextClass: 'button-unwind',
				cancelText: 'Cancel',
				cancelTextClass: 'button-accept',
			},
			postFunction: async () => {
				isBusy.value = true;

				try {
					const response = await api.documents.deleteDocumentById(row.id);
					if (!response?.data) throw new Error('No response from server.', { cause: response });

					props.fimenu.paperwork.uploadedDocuments = props.fimenu.paperwork.uploadedDocuments.filter((doc: IUploadedDocument) => doc.id !== row.id);
				} catch (err) {
					console.error('ERROR WHEN DELETING DOCUMENT: ', err);
					util.toastr('error', 'Could not delete document.', (err as Error)?.message ?? 'An unknown server error occurred.');
				} finally {
					isBusy.value = false;
				}
			},
		});
	};
	// #endregion

	// #region Current View
	const currentScreen = ref<CurrentView>(CurrentView.DocsList);
	const uploadMethod = ref<'saturn' | 'mobile'>(null);

	const changeScreenTo = (newView: CurrentView) => {
		currentScreen.value = newView;

		if (currentScreen.value === CurrentView.DocsList) resetCurrentDoc();
	};

	const headerActions = computed(
		(): {
			text?: string;
			icon?: string;
			class?: string;
			onClick?: () => unknown;
			disabled?: boolean;
		}[] => {
			if (props.fimenu.isSpectator) return null;

			if (currentScreen.value === CurrentView.DocsList) {
				return [
					{
						icon: 'fas fa-sync',
						class: 'button-info',
						onClick: getDocumentList,
					},
					{
						text: 'Upload New Document',
						disabled: isBusy.value,
						onClick: () => changeScreenTo(CurrentView.UploadDoc),
					},
				];
			}

			const actions = [];

			if (currentScreen.value === CurrentView.UploadDoc) {
				actions.push({
					text: 'Cancel',
					class: 'button-unwind',
					onClick: () => changeScreenTo(CurrentView.DocsList),
					disabled: isBusy.value,
				});

				if (uploadMethod.value === 'mobile') {
					actions.push({
						text: 'Send Link',
						class: 'button-edit hint-click',
						disabled: v$.value.$invalid || isBusy.value,
						onClick: openSendLinkToCustomerModal,
					});
				} else {
					actions.push({
						text: isBusy.value ? 'Loading...' : 'Save',
						class: 'button-save hint-click',
						onClick: addNewDocument,
						disabled: v$.value.$invalid || isBusy.value,
					});
				}
			}

			if (currentScreen.value === CurrentView.EditAssociations) {
				const isAddingAssociation = associationsToAdd.value.length > 0;
				const isDeletingAssociation = associationsToDelete.value.length > 0;

				actions.push(
					{
						text: 'Cancel',
						class: 'button-unwind',
						onClick: () => closeAssociationsEditor(),
						disabled: isBusy.value,
					},
					{
						text: isBusy.value ? 'Loading...' : 'Save',
						class: 'button-save hint-click',
						disabled: (!isAddingAssociation && !isDeletingAssociation) || isBusy.value,
						onClick: () => saveEditsToAssociationList(),
					},
				);
			}

			return actions;
		},
	);

	// #endregion

	// #region FIMenu Documents
	const docList = computed((): IUploadedDocument[] => {
		return props.fimenu.paperwork?.uploadedDocuments ?? [];
	});

	const getDocumentList = async () => {
		isBusy.value = true;

		try {
			await props.fimenu.getUploadedDocuments();
		} catch (err) {
			console.error('ERROR FETCHING UPLOADED DOCUMENTS: ', err);
			util.toastr('error', 'Error Retrieving Documents.', (err as Error)?.message ?? 'An unknown server error occurred.');
		} finally {
			isBusy.value = false;
		}
	};
	// #endregion

	// #region Associations Editor
	const selectedDocToEdit = ref<IUploadedDocument>(null);
	const newAssociationType = ref<DocAssociation>(null);
	const newAssociationId = ref<string>(null);

	const associationsToDelete = ref<DocumentAssociation[]>([]);
	const associationsToAdd = ref<DocumentAssociation[]>([]);

	const fullAssociationsList = computed((): DocumentAssociation[] => {
		if (!selectedDocToEdit.value) return [];

		let currentList = selectedDocToEdit.value.associations ?? [];

		currentList = currentList.filter(a => associationsToDelete.value.every(da => da.id !== a.id));
		currentList.push(...associationsToAdd.value);

		return currentList;
	});

	const openAssociationsEditor = (doc: IUploadedDocument) => {
		selectedDocToEdit.value = doc;
		currentScreen.value = CurrentView.EditAssociations;
	};

	const closeAssociationsEditor = () => {
		selectedDocToEdit.value = null;
		associationsToAdd.value = [];
		associationsToDelete.value = [];
		currentScreen.value = CurrentView.DocsList;
	};

	const addNewAssociation = () => {
		if (!newAssociationId.value || !newAssociationType.value) return;

		// Check if associations is already in list
		if (fullAssociationsList.value.some(a => a.id === newAssociationId.value && a.type === newAssociationType.value)) {
			util.toastr('error', 'Association Already Exists', "Can't add association. This association is already attached to this document.");
			return;
		}

		// If this association was in the delete list, filter it out
		if (associationsToDelete.value.some(a => a.id === newAssociationId.value && a.type === newAssociationType.value)) {
			associationsToDelete.value = associationsToDelete.value.filter(a => a.id !== newAssociationId.value && a.type !== newAssociationType.value);
		} else {
			associationsToAdd.value.push(new DocumentAssociation({ type: newAssociationType.value, id: newAssociationId.value }));
		}

		newAssociationId.value = null;
		newAssociationType.value = null;
	};

	const deleteAssociation = (association: DocumentAssociation) => {
		if (fullAssociationsList.value.length < 2) {
			util.toastr('error', "Can't Remove All Associations", "If you remove this association, this document's association list will be empty.");
			return;
		}

		if (associationsToAdd.value.some(a => a.id === association.id && a.type === association.type)) {
			// Deleting a newly added association
			associationsToAdd.value = associationsToAdd.value.filter(a => a.id !== association.id && a.type !== association.type);
		} else {
			// Deleting an existing association
			associationsToDelete.value.push(association);
		}
	};

	const saveEditsToAssociationList = async () => {
		if (associationsToDelete.value.length < 1 && associationsToAdd.value.length < 1) return;

		isBusy.value = true;
		try {
			const response = await api.documents.editDocumentAssociations(selectedDocToEdit.value.id, associationsToAdd.value, associationsToDelete.value);
			if (!response?.data) throw new Error('No response from server.', { cause: response });

			util.toastr('success', 'Successfully Saved Changes', 'Association list successfully updated.');
			closeAssociationsEditor();
			await getDocumentList();
		} catch (err) {
			console.error('ERROR SAVING ASSOCIATION CHANGES: ', err);
			util.toastr('error', 'Error Saving Changes', (err as Error)?.message ?? 'Unable to save changes to association list.');
		} finally {
			isBusy.value = false;
		}
	};
	// #endregion

	// #region Send Link
	const sendLinkTo = ref<PersonType>(null);

	const sendToList = computed((): { display: string; personType: PersonType }[] => {
		const list = [];

		if (props.fimenu.customer.id)
			list.push({
				display: props.fimenu.customer.fullName,
				personType: PersonType.Customer,
			});
		if (props.fimenu.coCustomer.id)
			list.push({
				display: props.fimenu.coCustomer.fullName,
				personType: PersonType.CoCustomer,
			});

		list.push({
			display: getFormattedEmployeeName(),
			personType: PersonType.FIManager,
		});

		return list;
	});

	const portalLinkRequest = computed((): IGetDocumentUploadPortalLinkRequest => {
		const associations = [new DocumentAssociation({ type: DocAssociation.Deal, id: props.fimenu.id })];
		let createdBy: string = null;
		let creatorType: DocAssociation = null;
		let creatorId: string = null;

		switch (sendLinkTo.value) {
			case PersonType.Customer:
				createdBy = props.fimenu.customer.fullName;
				creatorType = DocAssociation.Customer;
				creatorId = props.fimenu.customer.id;
				break;
			case PersonType.CoCustomer:
				createdBy = props.fimenu.coCustomer.fullName;
				creatorType = DocAssociation.Customer;
				creatorId = props.fimenu.coCustomer.id;
				break;
			case PersonType.FIManager:
				createdBy = getFormattedEmployeeName();
				creatorType = DocAssociation.Employee;
				creatorId = EMPLOYEE_INFO.EmployeeId;
				break;
			default:
				throw new Error(`Unhandled person type: ${PersonType[sendLinkTo.value]}. Could not generate link.`);
		}

		// Only add extra association if sending link to a customer
		if (creatorType === DocAssociation.Customer) associations.push(new DocumentAssociation({ type: creatorType, id: creatorId }));

		const request: IGetDocumentUploadPortalLinkRequest = {
			docType: currentDoc.value.docType,
			allowedFileTypes: ['application/pdf'],
			associations: associations,
			createdBy: createdBy,
			creatorType: creatorType,
			creatorId: creatorId,
		};

		return request;
	});

	const generatePortalLink = async (): Promise<string> => {
		isBusy.value = true;
		try {
			const response = await api.documents.getPortalLink(portalLinkRequest.value);
			if (!response?.data) throw new Error('No response from server.', { cause: response });

			return response.data;
		} catch (err) {
			console.error('UNABLE TO GENERATE DOCUMENT PORTAL LINK: ', err);
			util.toastr('error', "Can't Generate Link", (err as Error)?.message ?? 'An unknown server error occurred.');
		} finally {
			isBusy.value = false;
		}
	};

	const sendLinkToPortal = async (sendMethod: 'email' | 'text') => {
		isBusy.value = true;
		try {
			let response: AxiosResponse<ISimpleResponse> = null;
			if (sendLinkTo.value === PersonType.FIManager) {
				response = await api.documents.sendEmployeePortalLink(props.fimenu.storeCode, sendMethod, portalLinkRequest.value);
			} else {
				const customerId = sendLinkTo.value === PersonType.Customer ? props.fimenu.customer.id : props.fimenu.coCustomer.id;
				response = await api.documents.sendCustomerPortalLink(props.fimenu.storeCode, customerId, sendMethod, portalLinkRequest.value);
			}

			if (!response?.data) throw new Error('No response from server.', { cause: response });
			if (!response.data.success) throw new Error(response.data.error?.message ?? 'An unknown server error occurred.', { cause: response.data.error });

			util.toastr('success', 'Successfully Sent Link!', `The document upload link has been successfully sent via ${sendMethod}.`);
		} catch (err) {
			console.error('ERROR SENDING PORTAL LINK: ', err);
			util.toastr('error', 'Unable to Send Link to Portal', (err as Error)?.message ?? 'An unknown server error occurred.');
		} finally {
			isBusy.value = false;
		}
	};

	const openSendLinkToCustomerModal = () => {
		$modal.open(modalSendLinkToCustomer, {
			name: 'modalSendLinkToCustomer',
			passedData: {
				title: 'Send Link to Document Upload Portal',
				linkNoun: 'Document Upload',
				generateLink: generatePortalLink,
				abilityToSendCodeToGM: false,
			},
			postFunction: sendLinkToPortal,
		});
	};
	// #endregion

	// #region Validations
	const v$ = useVuelidate(
		{
			sendLinkTo: {
				required: requiredIf(() => uploadMethod.value === 'mobile' && currentScreen.value === CurrentView.UploadDoc),
			} as any,
			uploadMethod: { required } as any,
			currentDoc: {
				fileData: { required: requiredIf(() => uploadMethod.value === 'saturn' && currentScreen.value === CurrentView.UploadDoc) },
				docType: { required },
			} as any,
		},
		{ currentDoc, uploadMethod, sendLinkTo },
		{ $scope: false },
	);
	// #endregion

	// #region Other Helper Methods
	const getFormattedEmployeeName = (): string => {
		const employeeNameSplit = EMPLOYEE_INFO.EmployeeName.split(', ');
		return employeeNameSplit.length > 1 ? `${employeeNameSplit[1]} ${employeeNameSplit[0]}` : employeeNameSplit[0];
	};

	const getAssociationDisplay = (association: DocumentAssociation): string => {
		if (association.type === DocAssociation.Customer) {
			switch (association.id) {
				case props.fimenu.customer.id:
					return props.fimenu.customer.fullName;
				case props.fimenu.coCustomer.id:
					return props.fimenu.coCustomer.fullName;
				default:
					return props.fimenu.customersInfo?.customers?.find(ci => ci.id === association.id)?.fullName ?? 'Customer';
			}
		}

		if (association.type === DocAssociation.VIN) return `VIN:${association.id}`;

		if (association.type === DocAssociation.Store) return `Store:${association.id}`;

		if (association.type === DocAssociation.Deal) return association.id === props.fimenu.id ? 'This Deal' : 'External Deal';

		const enumKey = DocAssociation[association.type];
		return util.formatEnumKey(enumKey, true);
	};

	const formatFileNameForAzure = (document: ICurrentDocData): string => {
		try {
			return util.formatFileNameForAzure(document.fileName, document.fileType);
		} catch (err) {
			// Error will get thrown if filename contains any reserve words. Use a safe filename and try again.
			return util.formatFileNameForAzure(`${document.docType}_${Date.now()}`, document.fileType);
		}
	};

	const isAssociatedWithDeal = (associations: DocumentAssociation[]): boolean => {
		return associations.some(a => a.id === props.fimenu.id);
	};
	// #endregion
</script>
<style>
	.panel-uploaded-docs .docs-list .document-actions {
		display: flex;
		justify-content: space-evenly;
		gap: 10px;
	}

	.panel-uploaded-docs .docs-list button.button-edit-doc {
		color: var(--main-color);
	}

	.panel-uploaded-docs .docs-list .document-actions button:disabled,
	.panel-uploaded-docs .docs-list button.button-edit-doc:disabled {
		opacity: 0.5;
	}

	.panel-uploaded-docs .docs-list .document-actions button.button-delete-doc {
		color: var(--error-color);
	}

	.panel-uploaded-docs .docs-list .document-actions button.button-delete-doc:disabled {
		color: var(--error-color-hover);
	}

	.panel-uploaded-docs .associations-cell > .slot-wrapper {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 10px;
	}

	.panel-uploaded-docs .associations-tag {
		height: fit-content;
		padding: 5px;
		color: var(--button-color);
		background-color: var(--main-color);
		border-radius: var(--default-border-radius);
		filter: hue-rotate(calc(var(--index) * 30deg));
	}

	.panel-uploaded-docs .docs-add-edit .docs-add-edit-fieldset {
		display: flex;
	}

	.panel-uploaded-docs .docs-add-edit .docs-add-edit-fieldset > * {
		margin: 0 5px;
	}

	.panel-uploaded-docs .docs-add-edit .docs-add-edit-fieldset .file-metadata-form {
		flex-basis: 60%;
	}

	.panel-uploaded-docs .docs-add-edit .docs-add-edit-fieldset .file-metadata-form > * {
		margin: 10px 0;
	}

	.panel-uploaded-docs .docs-add-edit .docs-add-edit-fieldset .file-metadata-form input {
		text-transform: none;
	}

	.panel-uploaded-docs .docs-add-edit .docs-add-edit-fieldset .file-upload-form {
		flex-basis: 40%;
		max-width: 40%;
	}

	.panel-uploaded-docs .docs-associations-editor .basic-doc-info {
		display: flex;
		gap: 10px;
	}

	.panel-uploaded-docs .docs-associations-editor .list-of-associations {
		display: flex;
		gap: 10px;
		margin-bottom: 10px;
	}

	.panel-uploaded-docs .docs-associations-editor .list-of-associations .associations-tag button {
		width: fit-content;
	}

	.panel-uploaded-docs .docs-associations-editor .list-of-associations .associations-tag:has(button:disabled) {
		opacity: 70%;
	}

	.panel-uploaded-docs .docs-associations-editor .add-new-association {
		display: flex;
		align-items: center;
		gap: 10px;
	}

	.panel-uploaded-docs .docs-associations-editor .add-new-association button {
		width: fit-content;
	}

	.panel-uploaded-docs .docs-associations-editor .add-new-association .input-component input,
	.panel-uploaded-docs .docs-associations-editor .add-new-association .input-component select,
	.panel-uploaded-docs .docs-associations-editor .add-new-association .input-component .ird-container {
		text-transform: none;
	}
</style>
